import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-12">
    <h2 className="font-serif text-2xl sm:text-3xl mb-4 leading-tight">{title}</h2>
    <div className="space-y-4 text-sm sm:text-[15px] leading-relaxed text-neutral-700">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  const updated = 'May 2026';

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy — Zebra One Gallery"
        description="How Zebra One Gallery collects, uses and protects personal data under UK GDPR and the Data Protection Act 2018."
        canonical="https://zebraonegallery.com/privacy"
      />
      <Header />

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="privacy-policy-page">
        <div className="mb-12 border-b border-border pb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-xs text-neutral-500 mt-4">Last updated: {updated}</p>
        </div>

        <Section title="Who we are">
          <p>
            Zebra One Gallery Ltd ("we", "us", "our") is an independent
            contemporary art gallery based in Hampstead, London. We are the
            data controller for the personal information you share with us
            through this website (zebraonegallery.com), by email, by phone,
            or in person at the gallery.
          </p>
          <p>
            <strong>Contact:</strong> info@zebraonegallery.com · 020 7794 1281 ·
            Zebra One Gallery, Hampstead, London NW3 1QX, United Kingdom.
          </p>
        </Section>

        <Section title="What data we collect">
          <p>We only collect personal data we genuinely need:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Contact details</strong> — name, email, phone — when you submit an enquiry, consignment, consultation request or mailing-list signup.</li>
            <li><strong>Order details</strong> — shipping address and billing details — when you purchase or place a hold on an artwork.</li>
            <li><strong>Payment data</strong> — processed directly by Stripe; we never see your full card number.</li>
            <li><strong>Consignment images</strong> — photographs you upload as part of an authentication enquiry.</li>
            <li><strong>Site analytics</strong> — anonymous pages-viewed and device data, only if you accept analytics cookies.</li>
          </ul>
        </Section>

        <Section title="How we use it">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To respond to enquiries, valuations, consignment and consultation requests.</li>
            <li>To fulfil and ship orders, and to provide aftercare (provenance, condition).</li>
            <li>To send our newsletter and exhibition invitations — only if you've opted in. You can unsubscribe at any time.</li>
            <li>To meet our legal and tax obligations (HMRC, anti-money-laundering checks on high-value sales).</li>
          </ul>
        </Section>

        <Section title="Legal basis (UK GDPR)">
          <p>
            We process your data on one of four bases: (a) your <em>consent</em> (for marketing
            emails and optional cookies); (b) <em>contract performance</em> (to deliver an artwork
            you've bought or process a consignment); (c) <em>legitimate interests</em> (to reply to
            an enquiry, prevent fraud); (d) <em>legal obligation</em> (tax records, AML checks).
          </p>
        </Section>

        <Section title="Who we share it with">
          <p>We share data only with trusted processors who help us operate the gallery:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Stripe (card processing) · Mailchimp (newsletters) · DPD / FedEx (shipping)</li>
            <li>HMRC and our accountants where legally required</li>
            <li>Authentication bodies (e.g., Pest Control for Banksy) when you've asked us to handle authentication on your behalf</li>
          </ul>
          <p>We never sell your data. We do not transfer data outside the UK/EEA except via processors covered by Standard Contractual Clauses or an adequacy decision.</p>
        </Section>

        <Section title="How long we keep it">
          <p>
            Enquiries are kept for up to 24 months unless you become a client.
            Sales records are kept for 7 years (HMRC requirement). Marketing
            subscribers are kept until you unsubscribe. Consignment images are
            kept for as long as the artwork remains in our care plus 3 years.
          </p>
        </Section>

        <Section title="Your rights">
          <p>Under UK GDPR you can:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Request a copy of the data we hold about you</li>
            <li>Ask us to correct or delete it</li>
            <li>Object to processing or withdraw consent</li>
            <li>Lodge a complaint with the <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer" className="underline">Information Commissioner's Office</a></li>
          </ul>
          <p>To exercise any right, email <a href="mailto:info@zebraonegallery.com" className="underline">info@zebraonegallery.com</a>. We respond within 30 days.</p>
        </Section>

        <Section title="Cookies">
          <p>
            For details on the cookies we use, see our <Link to="/cookies" className="underline">cookie policy</Link>.
            You can revisit your preferences at any time using the "Cookies" link in the footer.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            We'll update this notice when our practices change. Material
            changes will be announced on the homepage and (where you've
            given consent) by email.
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
